import { apiClient } from "../../shared/api/apiClient";

export interface SettingsResponse<T = Record<string, any>> {
  success: boolean;
  message?: string;
  category: string;
  data: T;
  updated_at?: string;
  updated_by?: string;
}

export interface JobTelemetryEntry {
  id: string;
  name: string;
  queue: string;
  worker: string;
  status: "Running" | "Pending" | "Completed" | "Failed" | "Cancelled";
  scheduled_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  duration: string | null;
  retries: number;
  error_message?: string;
  telemetry_source?: string;
}

export interface JobTelemetrySummary {
  running: number;
  pending: number;
  completed: number;
  failed: number;
  total: number;
  worker_status: string;
}

export interface JobTelemetryResponse {
  success: boolean;
  summary: JobTelemetrySummary;
  jobs: JobTelemetryEntry[];
  unsupported_metrics?: string[];
}

export interface StorageProviderInfo {
  backend_type: string;
  r2_configured: boolean;
  r2_connected: boolean;
  r2_bucket_name: string;
  public_url: string;
  status_text: string;
  error_detail?: string | null;
}

export interface StorageMetrics {
  tenant_media: {
    file_count: number;
    size_bytes: number;
    formatted_size: string;
    path_prefix: string;
  };
  local_backups: {
    file_count: number;
    size_bytes: number;
    formatted_size: string;
    path: string;
  };
  backup_jobs_audit: {
    total_recorded: number;
    offsite_r2_count: number;
    latest_completed: string | null;
  };
}

export interface StorageTelemetryResponse {
  success: boolean;
  tenant_schema: string;
  provider: StorageProviderInfo;
  metrics: StorageMetrics;
  unsupported_metrics?: string[];
}

export interface DatabaseTableStat {
  name: string;
  rows: number;
  size_bytes: number;
  formatted_size: string;
  indexes: number;
  last_vacuum: string;
}

export interface DatabaseTelemetryResponse {
  success: boolean;
  tenant_schema: string;
  connection: {
    status: string;
    connected: boolean;
    database_name: string;
    engine: string;
    version: string;
    error_detail?: string | null;
  };
  metrics: {
    total_db_size_bytes: number;
    formatted_db_size: string;
    total_tables_count: number;
    total_rows_estimate: number;
    applied_migrations: number;
    latest_migration_applied: string | null;
  };
  connection_pool: {
    max_connections: number;
    active: number;
    idle: number;
    waiting: number;
    total: number;
  };
  tables: DatabaseTableStat[];
  unsupported_metrics?: string[];
}

export interface DRChecklistItem {
  id: string;
  step: string;
  verified: boolean;
  evidence: string;
  description: string;
}

export interface DREmergencyContact {
  name: string;
  role: string;
  email: string;
  phone: string;
}

export interface DisasterRecoverySummaryResponse {
  success: boolean;
  tenant_schema: string;
  dr_status: {
    code: "READY" | "WARNING" | "DEGRADED" | "UNAVAILABLE";
    reasons: string[];
    last_evaluated: string;
  };
  backup_summary: {
    latest_successful: {
      id: string;
      file_name: string;
      size_formatted: string;
      provider: string;
      started_at: string;
    } | null;
    latest_failed: {
      id: string;
      file_name: string;
      error_message: string | null;
      started_at: string;
    } | null;
    latest_r2_offsite: {
      id: string;
      file_name: string;
      completed_at: string | null;
    } | null;
    total_count: number;
  };
  restore_summary: {
    latest_job: {
      id: string;
      mode: string;
      status: string;
      started_at: string;
    } | null;
    dr_drills_count: number;
  };
  checklist: DRChecklistItem[];
  emergency_contacts: {
    configured: boolean;
    contacts: DREmergencyContact[];
    note: string;
  };
  unsupported_metrics?: string[];
}

/**
 * Fetches persistent system configuration settings for a category.
 */
export async function fetchCategorySettings<T = Record<string, any>>(
  category: string
): Promise<SettingsResponse<T>> {
  const response = await apiClient.get<SettingsResponse<T>>(`/system/settings/${category}/`);
  return response.data;
}

/**
 * Updates persistent system configuration settings for a category.
 */
export async function updateCategorySettings<T = Record<string, any>>(
  category: string,
  data: Record<string, any>
): Promise<SettingsResponse<T>> {
  const response = await apiClient.patch<SettingsResponse<T>>(`/system/settings/${category}/`, { data });
  return response.data;
}

/**
 * Fetches real background job queue telemetry from the backend API.
 */
export async function fetchJobQueueTelemetry(): Promise<JobTelemetryResponse> {
  const response = await apiClient.get<JobTelemetryResponse>("/system/jobs/");
  return response.data;
}

/**
 * Fetches real storage telemetry (R2, media, local backups) from backend.
 */
export async function fetchStorageTelemetry(): Promise<StorageTelemetryResponse> {
  const response = await apiClient.get<StorageTelemetryResponse>("/system/storage/");
  return response.data;
}

/**
 * Fetches real PostgreSQL/Neon database telemetry from backend.
 */
export async function fetchDatabaseTelemetry(): Promise<DatabaseTelemetryResponse> {
  const response = await apiClient.get<DatabaseTelemetryResponse>("/system/database/");
  return response.data;
}

/**
 * Fetches real Disaster Recovery summary and evidence checklist from backend.
 */
export async function fetchDRSummary(): Promise<DisasterRecoverySummaryResponse> {
  const response = await apiClient.get<DisasterRecoverySummaryResponse>("/system/dr/summary/");
  return response.data;
}
